import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Avatar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TableContainerWithScrollShadow from 'components/TableContainerWithScrollShadow';
import { deleteRecord } from '../../hartUtils';

const DataRestrictionsManagement = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [restrictions, setRestrictions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRestrictions();
  }, []);

  const fetchRestrictions = async () => {
    setLoading(true);
    try {
      const response = await fetch('/hart/data_restrictions', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch restrictions (${response.status})`);
      }
      
      const data = await response.json();
      setRestrictions(data || []);
    } catch (error) {
      console.error('Error fetching data restrictions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (restriction) => {
    navigate(`/projects/${projectId}/hart/admin/data-restrictions/${restriction.id}/edit`);
  };

  const handleDelete = async (restriction) => {
    if (!window.confirm('Are you sure you want to delete this data restriction?')) {
      return;
    }
    try {
      await deleteRecord('/hart/data_restrictions', restriction.id);
      await fetchRestrictions();
    } catch (error) {
      console.error('Error deleting data restriction:', error);
      alert('Failed to delete data restriction. Please try again.');
    }
  };

  const handleCreateNew = () => {
    navigate(`/projects/${projectId}/hart/admin/data-restrictions/create`);
  };

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  const getAppliesTo = (restriction) => {
    return restriction.restriction_type === 'job' ? 'Job' : 'Client';
  };

  const getItemNames = (restriction) => {
    // Return array of job names or client names
    if (restriction.restriction_type === 'client' && restriction.clients && restriction.clients.length > 0) {
      return restriction.clients.map(client => 
        typeof client === 'object' ? client.name : client
      );
    } else if (restriction.restriction_type === 'job' && restriction.jobs && restriction.jobs.length > 0) {
      return restriction.jobs.map(job => 
        typeof job === 'object' ? `Job ${job.job_no}${job.job_name ? ' - ' + job.job_name : ''}` : job
      );
    }
    return [];
  };

  const filteredRestrictions = restrictions.filter(restriction => {
    const itemNames = getItemNames(restriction);
    const itemNamesStr = itemNames.join(' ');
    const matchesSearch =
      (restriction.name && restriction.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      itemNamesStr.toLowerCase().includes(searchQuery.toLowerCase());
    
    const appliesTo = getAppliesTo(restriction);
    const matchesType = typeFilter === 'all' || appliesTo === typeFilter;
    
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'Active' && !isExpired(restriction.expiration_date)) ||
      (statusFilter === 'expired' && isExpired(restriction.expiration_date));

    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
        <TextField
          label="Search"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputLabelProps={{ 
            shrink: true,
            sx: { color: 'primary.main', fontWeight: 600 }
          }}
          sx={{ flexGrow: 1 }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel sx={{ color: 'primary.main', fontWeight: 600 }}>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="Job">Job</MenuItem>
            <MenuItem value="Client">Client</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel sx={{ color: 'primary.main', fontWeight: 600 }}>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </Select>
        </FormControl>

        <Button variant="contained" onClick={handleCreateNew} sx={{ py: '8px' }}>
          + Create Data Restriction
        </Button>
      </Box>

      <TableContainerWithScrollShadow>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Applies To</TableCell>
              <TableCell>Allow List</TableCell>
              <TableCell>Deny List</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRestrictions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No data restrictions found
                </TableCell>
              </TableRow>
            ) : (
              filteredRestrictions.map((restriction) => {
                const itemNames = getItemNames(restriction);
                const displayItems = itemNames.length > 0 
                  ? itemNames.join(', ') 
                  : 'No items assigned';
                
                return (
                  <TableRow key={restriction.id} hover>
                    <TableCell>
                      <Box>
                        <Box sx={{ fontWeight: 500 }}>{restriction.name || 'Unnamed'}</Box>
                        <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                          {displayItems}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={getAppliesTo(restriction)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: '0.875rem',
                          bgcolor: (restriction.allow_list || []).length > 0 ? 'primary.main' : 'grey.400',
                          color: 'white',
                        }}
                      >
                        {(restriction.allow_list || []).length}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: '0.875rem',
                          bgcolor: (restriction.deny_list || []).length > 0 ? 'secondary.main' : 'grey.400',
                          color: 'white',
                        }}
                      >
                        {(restriction.deny_list || []).length}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      {restriction.expiration_date ? (
                        <Chip
                          label={new Date(restriction.expiration_date).toLocaleDateString()}
                          size="small"
                          sx={{
                            bgcolor: isExpired(restriction.expiration_date) ? 'grey.400' : '#17a2b8',
                            color: 'white',
                            '& .MuiChip-label': {
                              color: 'white',
                            },
                            '&:hover': {
                              bgcolor: isExpired(restriction.expiration_date) ? 'grey.400' : '#17a2b8',
                            },
                          }}
                        />
                      ) : (
                        '--'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(restriction)}
                        aria-label="Edit"
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(restriction)}
                        aria-label="Delete"
                        title="Delete"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainerWithScrollShadow>
    </Box>
  );
};

export default DataRestrictionsManagement;
